// src/components/lesson/useLessonComplete.ts
//
// Reports `lesson_complete` once per visit when the student has both reached
// the end of the lesson (the sentinel this hook's ref is attached to scrolled
// into view) and spent the minimum time on the page (src/lib/learn/
// lesson-complete.ts). Every browser API is guarded — the lesson page renders
// in environments without IntersectionObserver or sessionStorage.

'use client'

import { useCallback, useEffect, useRef } from 'react'
import { trackPathEvent } from '@/lib/analytics/track-path-event'
import { completionKey, shouldMarkComplete } from '@/lib/learn/lesson-complete'

const CHECK_EVERY_MS = 5_000

export function useLessonComplete(input: {
  slug: string
  /** The canonical step topic the event is labelled with. */
  topic: string
  estimatedReadMinutes?: number | null
  /** False disables reporting (a lesson off the path). */
  enabled?: boolean
}) {
  const { slug, topic, estimatedReadMinutes, enabled = true } = input
  const reachedEnd = useRef(false)
  const done = useRef(false)
  const mountedAt = useRef(Date.now())
  const observer = useRef<IntersectionObserver | null>(null)

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect()
    observer.current = null
    if (!node || typeof IntersectionObserver === 'undefined') return
    observer.current = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) reachedEnd.current = true
    })
    observer.current.observe(node)
  }, [])

  useEffect(() => {
    if (!enabled) return
    try {
      if (sessionStorage.getItem(completionKey(slug))) done.current = true
    } catch {
      // no sessionStorage — report at most once per mount instead
    }
    const tick = () => {
      if (done.current) return
      const secondsOnPage = (Date.now() - mountedAt.current) / 1000
      if (
        !shouldMarkComplete({ reachedEnd: reachedEnd.current, secondsOnPage, estimatedReadMinutes })
      )
        return
      done.current = true
      try {
        sessionStorage.setItem(completionKey(slug), '1')
      } catch {
        // ignore
      }
      trackPathEvent('lesson_complete', topic)
    }
    const timer = setInterval(tick, CHECK_EVERY_MS)
    const onHide = () => {
      if (document.visibilityState === 'hidden') tick()
    }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onHide)
      observer.current?.disconnect()
    }
  }, [enabled, slug, topic, estimatedReadMinutes])

  return sentinelRef
}
